import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Compass,
  Zap,
  ImageIcon,
  CheckSquare,
  Send,
  BarChart3,
  Settings,
  Bell,
  Menu,
  X,
  ChevronLeft,
  Sparkles,
  Building2,
  GitBranch,
  ShieldCheck,
  Wand2,
} from 'lucide-react';
import { ROUTE_PATHS } from '../lib/index';

interface LayoutProps {
  children: React.ReactNode;
}

const navigationItems = [
  { name: 'Dashboard',  path: ROUTE_PATHS.HOME,        icon: LayoutDashboard, group: 'core' },
  { name: 'Discover',   path: ROUTE_PATHS.DISCOVER,    icon: Compass,         group: 'core' },
  { name: 'Generate',   path: ROUTE_PATHS.GENERATE,    icon: Zap,             group: 'core' },
  { name: 'Assets',     path: ROUTE_PATHS.ASSETS,      icon: ImageIcon,       group: 'ops'  },
  { name: 'Approval',   path: ROUTE_PATHS.APPROVAL,    icon: CheckSquare,     group: 'ops'  },
  { name: 'Publish',    path: ROUTE_PATHS.PUBLISH,     icon: Send,            group: 'ops'  },
  { name: 'Analytics',  path: ROUTE_PATHS.ANALYTICS,   icon: BarChart3,       group: 'ops'  },
  { name: 'Brand',      path: ROUTE_PATHS.BRAND,       icon: Building2,       group: 'sys'  },
  { name: 'Canvas',     path: ROUTE_PATHS.CANVAS,      icon: GitBranch,       group: 'sys'  },
  { name: 'Guardrails', path: ROUTE_PATHS.GUARDRAILS,  icon: ShieldCheck,     group: 'sys'  },
  { name: 'Humanizer',  path: ROUTE_PATHS.HUMANIZER,   icon: Wand2,           group: 'sys'  },
  { name: 'Settings',   path: ROUTE_PATHS.SETTINGS,    icon: Settings,        group: 'sys'  },
];

const PAGE_TITLES: Record<string, { title: string; subtitle: string }> = {
  '/':            { title: 'Dashboard',           subtitle: 'Command Center Overview' },
  '/trends':      { title: 'Discover',             subtitle: 'Emerging Content Opportunities' },
  '/pipeline':    { title: 'Generate',             subtitle: 'Create Publish-Ready Content Assets' },
  '/assets':      { title: 'Asset Library',        subtitle: 'Manage & Organise Content' },
  '/approval':    { title: 'Approval Queue',       subtitle: 'Review & Brand Compliance' },
  '/distribution':{ title: 'Publish',              subtitle: 'Distribution Command Center' },
  '/analytics':   { title: 'Analytics',            subtitle: 'Content ROI & Lifecycle Tracking' },
  '/brand':       { title: 'Brand Intelligence',   subtitle: 'Knowledge Engine & Voice Configuration' },
  '/canvas':      { title: 'Canvas Designer',      subtitle: 'Node-Based Workflow Builder' },
  '/guardrails':  { title: 'Brand Guardrails',     subtitle: 'Global Tone & Content Constraints' },
  '/humanizer':   { title: 'AI Humanizer',         subtitle: 'Bypass Detection · Perplexity & Burstiness Engine' },
  '/settings':    { title: 'Settings',             subtitle: 'Platform Configuration' },
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  const pageInfo = PAGE_TITLES[location.pathname] || { title: 'Content Intelligence', subtitle: 'Factory' };

  const coreItems = navigationItems.filter(i => i.group === 'core');
  const opsItems  = navigationItems.filter(i => i.group === 'ops');
  const sysItems  = navigationItems.filter(i => i.group === 'sys');

  const NavGroup = ({ items, label, collapsed }: { items: typeof navigationItems; label: string; collapsed: boolean }) => (
    <div className="mb-1">
      {!collapsed && (
        <p className="px-3 py-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-600">{label}</p>
      )}
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) =>
              `flex items-center gap-3 mx-2 px-3 py-2.5 rounded-xl transition-all duration-150 group mb-0.5 ${
                isActive
                  ? 'bg-gradient-to-r from-violet-500/20 to-violet-600/10 text-violet-300 border border-violet-500/25 shadow-[0_0_12px_rgba(139,92,246,0.12)]'
                  : 'text-slate-500 hover:bg-white/[0.04] hover:text-slate-300 border border-transparent'
              } ${collapsed ? 'justify-center px-0 mx-auto w-10' : ''}`
            }
          >
            {({ isActive }) => (
              <>
                <Icon className={`w-[18px] h-[18px] flex-shrink-0 transition-colors ${isActive ? 'text-violet-400' : 'text-slate-500 group-hover:text-slate-300'}`} />
                {!collapsed && <span className="font-medium text-[13px] leading-none">{item.name}</span>}
                {!collapsed && isActive && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-violet-400 shadow-[0_0_6px_rgba(139,92,246,0.9)]" />
                )}
              </>
            )}
          </NavLink>
        );
      })}
    </div>
  );

  return (
    <div className="flex h-[100dvh] text-slate-100 overflow-hidden" style={{ backgroundColor: '#07070f' }}>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed md:relative inset-y-0 left-0 z-50
          flex flex-col
          bg-[#0f0f16]/70 backdrop-blur-md border-r border-white/[0.05]
          transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          ${sidebarCollapsed ? 'w-[60px]' : 'w-[220px]'}
          h-full flex-shrink-0
        `}
      >
        {/* Logo */}
        <div className={`h-14 flex items-center border-b border-white/[0.05] flex-shrink-0 px-3 ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-7 h-7 bg-gradient-to-br from-violet-500 via-purple-500 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-[0_0_12px_rgba(139,92,246,0.5)]">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-[11px] bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent leading-tight truncate">Content Intelligence</p>
                <p className="text-[9px] text-slate-600 leading-tight">Factory · Enterprise</p>
              </div>
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden md:flex p-1.5 hover:bg-slate-800/50 rounded-lg transition-colors text-slate-600 hover:text-slate-300 flex-shrink-0"
            title={sidebarCollapsed ? 'Expand' : 'Collapse'}
          >
            {sidebarCollapsed ? <Menu className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
          <button onClick={() => setSidebarOpen(false)} className="md:hidden p-1.5 hover:bg-slate-800/50 rounded-lg text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 overflow-y-auto scrollbar-hide">
          <NavGroup items={coreItems} label="Workflow" collapsed={sidebarCollapsed} />
          <div className="mx-2 my-2 border-t border-slate-800/50" />
          <NavGroup items={opsItems} label="Operations" collapsed={sidebarCollapsed} />
          <div className="mx-2 my-2 border-t border-slate-800/50" />
          <NavGroup items={sysItems} label="System" collapsed={sidebarCollapsed} />
        </nav>

        {/* Status footer */}
        {!sidebarCollapsed && (
          <div className="p-3 border-t border-white/[0.04] flex-shrink-0">
            <div className="bg-[#12121a]/80 rounded-xl p-2.5 border border-white/[0.06]">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                <span className="text-[11px] font-semibold text-slate-300">All Agents Active</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-600">3 running · Pipeline ready</span>
                <span className="text-[9px] font-mono text-violet-500">v2.0</span>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top header */}
        <header className="h-14 bg-[#0f0f16]/80 backdrop-blur-xl border-b border-white/[0.05] flex items-center justify-between px-4 flex-shrink-0 gap-3">
          <button onClick={() => setSidebarOpen(true)} className="md:hidden p-2 hover:bg-slate-800/50 rounded-lg text-slate-500">
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="md:hidden w-6 h-6 bg-gradient-to-br from-violet-500 to-cyan-500 rounded-md flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
            <div className="min-w-0 hidden sm:block">
              <p className="text-slate-100 font-semibold text-sm leading-tight truncate">{pageInfo.title}</p>
              <p className="text-slate-500 text-[11px] leading-tight truncate">{pageInfo.subtitle}</p>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full ml-1">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wide">Live</span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Quick action */}
            <button className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/20 rounded-lg transition-colors text-violet-400 text-xs font-medium">
              <Zap className="w-3.5 h-3.5" />
              <span>Quick Generate</span>
            </button>
            <button className="relative p-2 hover:bg-slate-800/50 rounded-lg transition-colors group">
              <Bell className="w-4 h-4 text-slate-500 group-hover:text-slate-300" />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-violet-500 rounded-full shadow-[0_0_4px_rgba(139,92,246,0.8)]" />
            </button>
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center ring-2 ring-violet-500/20">
              <span className="text-white text-[10px] font-bold">AD</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-slate-950 pb-16 md:pb-0">
          <div className="h-full">{children}</div>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-[#0d0f1a]/95 backdrop-blur-xl border-t border-slate-800/60 flex items-center justify-around px-1 pb-[env(safe-area-inset-bottom)]">
        {navigationItems.slice(0, 6).map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-1.5 py-2 rounded-lg transition-all ${
                  isActive ? 'text-violet-400' : 'text-slate-600 hover:text-slate-400'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={`w-5 h-5 ${isActive ? 'text-violet-400' : ''}`} />
                  <span className="text-[9px] font-medium leading-none">{item.name}</span>
                  {isActive && <div className="w-1 h-1 rounded-full bg-violet-400 mt-0.5" />}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
};

export default Layout;
